import React, { useState, useCallback, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import axios from 'axios'
dayjs.extend(isoWeek)
import { BASE_URL } from '@/constant'
import { prepareHeaders } from '@/lib/cookie'
import { DateRange } from '@/components/ReusableDateRangePicker'
import { Responsive } from 'react-grid-layout'
import type { Layout, ResponsiveLayouts } from 'react-grid-layout'
import {
  PanelLeftOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Layout as LayoutIcon,
  BarChart2,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Table as TableIcon,
  Sparkles,
  Trash2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  TrendingUp,
  Hash,
  Info,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import ReusableDateRangePicker from '@/components/ReusableDateRangePicker'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { type PlanStatus, WPPlanRow } from '@/redux/service/weeklyPlanning'

const ResponsiveGridLayout = Responsive

// Types
type ComponentType = 'bar' | 'line' | 'pie' | 'scorecard' | 'table'
type AggregationType = 'count' | 'sum' | 'avg' | 'min' | 'max'
type DashboardCellValue = string | number | null | undefined
type ChartDataRow = {
  name?: string
  value?: number
  label?: string
  [key: string]: DashboardCellValue
}

type StudioPlanRow = WPPlanRow & {
  week_start?: string | null
  week_end?: string | null
  week_label?: string
}

type WeeklyPlanningApiRow = Partial<Omit<StudioPlanRow, 'team'>> & {
  team?: string | null
}

interface WeeklyPlanningRangeUser {
  user_id: number
  member_name: string
  team_id: number | null
  team?: string | null
  achieved_billable_hrs?: string | number | null
  weeks?: WeeklyPlanningApiRow[]
}

type WeeklyPlanningPlansResponseItem =
  WeeklyPlanningApiRow | WeeklyPlanningRangeUser

interface WeeklyPlanningPlansResponse {
  success: boolean
  data?: WeeklyPlanningPlansResponseItem[]
}

interface FieldDefinition {
  key: string
  label: string
  type: 'text' | 'number'
}

const WEEKLY_PLANNING_FIELDS: FieldDefinition[] = [
  { key: 'member_name', label: 'Member Name', type: 'text' },
  { key: 'team', label: 'Team', type: 'text' },
  { key: 'week_label', label: 'Week', type: 'text' },
  { key: 'plan_status', label: 'Plan Status', type: 'text' },
  { key: 'est_billing_hrs', label: 'Est. Billing Hrs', type: 'number' },
  { key: 'est_non_billing_hrs', label: 'Est. Non-Billing Hrs', type: 'number' },
  { key: 'backlog_hrs', label: 'Backlog Hrs', type: 'number' },
  {
    key: 'achieved_billable_hrs',
    label: 'Achieved Billable Hrs',
    type: 'number',
  },
]

const TEXT_FIELDS = WEEKLY_PLANNING_FIELDS.filter(f => f.type === 'text')
const NUMBER_FIELDS = WEEKLY_PLANNING_FIELDS.filter(f => f.type === 'number')

interface DashboardItem {
  i: string
  type: ComponentType
  title: string
  dimensionField?: string
  metricFields?: string[]
  aggregation?: AggregationType
  labelField?: string
  valueField?: string
  selectedColumns?: string[]
  page?: number
  limit?: number
  totalRows?: number
  chartData?: ChartDataRow[]
  scorecardMetric?: string
  scorecardAggregation?: AggregationType
}

// Helpers & Utilities
const COLORS = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#14b8a6',
  '#84cc16',
  '#3b82f6',
  '#2563eb',
]

function getFieldLabel(key: string): string {
  return WEEKLY_PLANNING_FIELDS.find(f => f.key === key)?.label || key
}

function getPlanField(row: StudioPlanRow, field: string): unknown {
  return (row as unknown as Record<string, unknown>)[field]
}

function getNumericValue(row: StudioPlanRow, field: string): number {
  const raw = getPlanField(row, field)
  return typeof raw === 'string'
    ? parseFloat(raw) || 0
    : typeof raw === 'number'
      ? raw
      : 0
}

function formatTableValue(value: unknown): DashboardCellValue {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'string' || typeof value === 'number') return value
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function normalizePlanRow(row: WeeklyPlanningApiRow): StudioPlanRow {
  const base: WPPlanRow = {
    user_id: row.user_id ?? 0,
    member_name: row.member_name ?? 'Unknown',
    team_id: row.team_id ?? null,
    team: row.team ?? 'Unassigned',
    plan_id: row.plan_id ?? null,
    status: row.status ?? null,
    plan_status: (row.plan_status ?? 'no_plan') as PlanStatus,
    est_bench_date: row.est_bench_date ?? null,
    notes: row.notes ?? null,
    est_billing_hrs: String(row.est_billing_hrs ?? '0'),
    est_non_billing_hrs: String(row.est_non_billing_hrs ?? '0'),
    backlog_hrs: String(row.backlog_hrs ?? '0'),
    achieved_billable_hrs: String(row.achieved_billable_hrs ?? '0'),
  }

  return {
    ...base,
    ...(row.week_start !== undefined ? { week_start: row.week_start } : {}),
    ...(row.week_end !== undefined ? { week_end: row.week_end } : {}),
    ...(row.week_label !== undefined ? { week_label: row.week_label } : {}),
  }
}

function isRangeUser(
  item: WeeklyPlanningPlansResponseItem
): item is WeeklyPlanningRangeUser {
  return Array.isArray((item as WeeklyPlanningRangeUser).weeks)
}

function isApiRow(
  item: WeeklyPlanningPlansResponseItem
): item is WeeklyPlanningApiRow {
  return !isRangeUser(item)
}

function aggregate(values: number[], method: AggregationType): number {
  if (values.length === 0) return 0
  switch (method) {
    case 'count':
      return values.length
    case 'sum':
      return Math.round(values.reduce((a, b) => a + b, 0) * 10) / 10
    case 'avg':
      return (
        Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) /
        10
      )
    case 'min':
      return Math.min(...values)
    case 'max':
      return Math.max(...values)
    default:
      return values.length
  }
}

function buildChartData(
  plans: StudioPlanRow[],
  dimensionField: string,
  metricFields: string[],
  aggregation: AggregationType = 'sum'
): ChartDataRow[] {
  if (!dimensionField || metricFields.length === 0 || plans.length === 0)
    return []

  const groups = new Map<string, StudioPlanRow[]>()
  for (const p of plans) {
    const dimValue = String(getPlanField(p, dimensionField) || 'Unknown')
    if (!groups.has(dimValue)) groups.set(dimValue, [])
    groups.get(dimValue)!.push(p)
  }

  const result: ChartDataRow[] = []
  for (const [name, groupPlans] of groups) {
    const point: ChartDataRow = { name }
    for (const metric of metricFields) {
      const values = groupPlans.map(p => getNumericValue(p, metric))
      point[getFieldLabel(metric)] = aggregate(values, aggregation)
    }
    result.push(point)
  }

  if (metricFields.length > 0) {
    const firstLabel = getFieldLabel(metricFields[0] ?? '')
    result.sort(
      (a, b) => Number(b[firstLabel] || 0) - Number(a[firstLabel] || 0)
    )
  }

  return result.slice(0, 20)
}

function buildPieData(
  plans: StudioPlanRow[],
  labelField: string,
  valueField: string | undefined,
  aggregation: AggregationType = 'count'
): { name: string; value: number }[] {
  if (!labelField || plans.length === 0) return []

  const groups = new Map<string, WPPlanRow[]>()
  for (const p of plans) {
    const label = String(getPlanField(p, labelField) || 'Unknown')
    if (!groups.has(label)) groups.set(label, [])
    groups.get(label)!.push(p)
  }

  const result: { name: string; value: number }[] = []
  for (const [name, groupPlans] of groups) {
    let value: number
    if (aggregation === 'count' || !valueField) {
      value = groupPlans.length
    } else {
      const values = groupPlans.map(p => getNumericValue(p, valueField))
      value = aggregate(values, aggregation)
    }
    result.push({ name, value })
  }

  return result.sort((a, b) => b.value - a.value).slice(0, 12)
}

function buildScorecardData(
  plans: StudioPlanRow[],
  metric: string,
  aggregation: AggregationType = 'sum'
): { value: number; label: string } {
  if (!metric || plans.length === 0) return { value: 0, label: '' }

  const values = plans.map(p => getNumericValue(p, metric))
  return {
    value: aggregate(values, aggregation),
    label: getFieldLabel(metric),
  }
}

function buildTableData(
  plans: StudioPlanRow[],
  columns: string[],
  page: number = 1,
  limit: number = 10
): { rows: ChartDataRow[]; total: number } {
  if (columns.length === 0) return { rows: [], total: 0 }

  const start = (page - 1) * limit
  const slice = plans.slice(start, start + limit)

  const rows = slice.map(p => {
    const row: ChartDataRow = {}
    for (const col of columns) {
      row[col] = formatTableValue(getPlanField(p, col))
    }
    return row
  })

  return { rows, total: plans.length }
}

function recomputeItemData(
  item: DashboardItem,
  plans: StudioPlanRow[]
): DashboardItem {
  switch (item.type) {
    case 'bar':
    case 'line':
      return {
        ...item,
        chartData: buildChartData(
          plans,
          item.dimensionField || '',
          item.metricFields || [],
          item.aggregation || 'sum'
        ),
      }
    case 'pie':
      return {
        ...item,
        chartData: buildPieData(
          plans,
          item.labelField || '',
          item.valueField,
          item.aggregation || 'count'
        ),
      }
    case 'scorecard': {
      const sc = buildScorecardData(
        plans,
        item.scorecardMetric || '',
        item.scorecardAggregation || 'sum'
      )
      return { ...item, chartData: [sc] }
    }
    case 'table': {
      const td = buildTableData(
        plans,
        item.selectedColumns || [],
        item.page || 1,
        item.limit || 10
      )
      return { ...item, chartData: td.rows, totalRows: td.total }
    }
    default:
      return item
  }
}

// Config Panel Component
interface ConfigPanelProps {
  selectedItem: DashboardItem
  onUpdate: (id: string, updates: Partial<DashboardItem>) => void
}

const AGGREGATION_OPTIONS: { value: AggregationType; label: string }[] = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
]

const SelectInput: React.FC<{
  label: string
  value: string
  onChange: (val: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
  icon?: React.ReactNode
}> = ({ label, value, onChange, options, placeholder = 'Select...', icon }) => (
  <div className='space-y-2'>
    <label className='text-[11px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5'>
      {icon}
      {label}
    </label>
    <div className='relative'>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className='w-full appearance-none border border-border rounded-lg px-3 py-2.5 text-sm text-foreground bg-background hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer'
      >
        <option value='' disabled>
          {placeholder}
        </option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground'>
        <svg
          className='h-4 w-4 fill-current'
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 20 20'
        >
          <path d='M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z' />
        </svg>
      </div>
    </div>
  </div>
)

const CheckboxField: React.FC<{
  label: string
  checked: boolean
  onChange: () => void
}> = ({ label, checked, onChange }) => (
  <label
    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
      checked ? 'bg-primary/5 ring-1 ring-primary/20' : 'hover:bg-muted/50'
    }`}
  >
    <input
      type='checkbox'
      checked={checked}
      onChange={onChange}
      className='w-4 h-4 rounded border-border text-primary focus:ring-primary/30 cursor-pointer'
    />
    <span
      className={`text-sm ${checked ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
    >
      {label}
    </span>
  </label>
)

export const ConfigPanel: React.FC<ConfigPanelProps> = ({
  selectedItem,
  onUpdate,
}) => {
  const id = selectedItem.i

  const renderBarLineConfig = () => (
    <>
      <SelectInput
        label='Group By (Dimension)'
        value={selectedItem.dimensionField || ''}
        onChange={val => onUpdate(id, { dimensionField: val })}
        options={TEXT_FIELDS.map(f => ({ value: f.key, label: f.label }))}
        placeholder='Select dimension...'
      />

      <SelectInput
        label='Aggregation'
        icon={<Hash size={12} />}
        value={selectedItem.aggregation || 'sum'}
        onChange={val => onUpdate(id, { aggregation: val as AggregationType })}
        options={AGGREGATION_OPTIONS}
      />

      <div className='space-y-2'>
        <label className='text-[11px] font-semibold text-muted-foreground uppercase tracking-widest block'>
          Metric Fields
        </label>
        <div className='bg-muted/30 rounded-lg p-2 space-y-0.5 border border-border/50 max-h-[200px] overflow-y-auto'>
          {NUMBER_FIELDS.map(field => (
            <CheckboxField
              key={field.key}
              label={field.label}
              checked={selectedItem.metricFields?.includes(field.key) || false}
              onChange={() => {
                const current = selectedItem.metricFields || []
                const updated = current.includes(field.key)
                  ? current.filter(f => f !== field.key)
                  : [...current, field.key]
                onUpdate(id, { metricFields: updated })
              }}
            />
          ))}
        </div>
        {(!selectedItem.metricFields ||
          selectedItem.metricFields.length === 0) && (
          <p className='text-xs text-amber-500 flex items-center gap-1 mt-1'>
            <span className='w-1.5 h-1.5 rounded-full bg-amber-500' />
            Select at least one metric field
          </p>
        )}
      </div>
    </>
  )

  const renderPieConfig = () => (
    <>
      <SelectInput
        label='Label Field (Dimension)'
        value={selectedItem.labelField || ''}
        onChange={val => onUpdate(id, { labelField: val })}
        options={TEXT_FIELDS.map(f => ({ value: f.key, label: f.label }))}
        placeholder='Select label field...'
      />

      <SelectInput
        label='Aggregation'
        icon={<Hash size={12} />}
        value={selectedItem.aggregation || 'count'}
        onChange={val => onUpdate(id, { aggregation: val as AggregationType })}
        options={[
          { value: 'count', label: 'Count (number of rows)' },
          { value: 'sum', label: 'Sum (of metric)' },
        ]}
      />

      {selectedItem.aggregation === 'sum' && (
        <SelectInput
          label='Value Field (Metric)'
          value={selectedItem.valueField || ''}
          onChange={val => onUpdate(id, { valueField: val })}
          options={NUMBER_FIELDS.map(f => ({ value: f.key, label: f.label }))}
          placeholder='Select value field...'
        />
      )}

      {!selectedItem.labelField && (
        <p className='text-xs text-amber-500 flex items-center gap-1'>
          <span className='w-1.5 h-1.5 rounded-full bg-amber-500' />
          Select a label field to display pie chart
        </p>
      )}
    </>
  )

  const renderScorecardConfig = () => (
    <>
      <SelectInput
        label='Metric'
        value={selectedItem.scorecardMetric || ''}
        onChange={val => onUpdate(id, { scorecardMetric: val })}
        options={NUMBER_FIELDS.map(f => ({ value: f.key, label: f.label }))}
        placeholder='Select metric...'
      />

      <SelectInput
        label='Aggregation'
        icon={<Hash size={12} />}
        value={selectedItem.scorecardAggregation || 'sum'}
        onChange={val =>
          onUpdate(id, { scorecardAggregation: val as AggregationType })
        }
        options={AGGREGATION_OPTIONS}
      />
    </>
  )

  const renderTableConfig = () => (
    <>
      <div className='space-y-2'>
        <label className='text-[11px] font-semibold text-muted-foreground uppercase tracking-widest block'>
          Columns
        </label>
        <div className='bg-muted/30 rounded-lg p-2 space-y-0.5 border border-border/50 max-h-[280px] overflow-y-auto'>
          {WEEKLY_PLANNING_FIELDS.map(field => (
            <CheckboxField
              key={field.key}
              label={field.label}
              checked={
                selectedItem.selectedColumns?.includes(field.key) || false
              }
              onChange={() => {
                const current = selectedItem.selectedColumns || []
                const updated = current.includes(field.key)
                  ? current.filter(f => f !== field.key)
                  : [...current, field.key]
                onUpdate(id, { selectedColumns: updated })
              }}
            />
          ))}
        </div>
      </div>

      <SelectInput
        label='Rows Per Page'
        value={String(selectedItem.limit || 10)}
        onChange={val => onUpdate(id, { limit: parseInt(val), page: 1 })}
        options={[
          { value: '5', label: '5 rows' },
          { value: '10', label: '10 rows' },
          { value: '20', label: '20 rows' },
          { value: '50', label: '50 rows' },
        ]}
      />
    </>
  )

  return (
    <div className='w-80 bg-card border-l border-border flex flex-col shadow-xl z-20 shrink-0'>
      <div className='p-4 border-b border-border flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='p-1.5 rounded-lg bg-primary/10 text-primary'>
            {selectedItem.type === 'table' ? (
              <TableIcon size={14} />
            ) : (
              <BarChart2 size={14} />
            )}
          </div>
          <h2 className='text-sm font-bold text-foreground'>Configure</h2>
        </div>
        <span className='text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium capitalize'>
          {selectedItem.type}
        </span>
      </div>

      <div className='flex-1 overflow-y-auto p-5 space-y-6 pb-32'>
        <div className='space-y-2'>
          <label className='text-[11px] font-semibold text-muted-foreground uppercase tracking-widest block'>
            Title
          </label>
          <input
            type='text'
            value={selectedItem.title}
            onChange={e => onUpdate(id, { title: e.target.value })}
            className='w-full border border-border rounded-lg px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
            placeholder='Enter title...'
          />
        </div>

        <hr className='border-border/50' />

        {(selectedItem.type === 'bar' || selectedItem.type === 'line') &&
          renderBarLineConfig()}
        {selectedItem.type === 'pie' && renderPieConfig()}
        {selectedItem.type === 'scorecard' && renderScorecardConfig()}
        {selectedItem.type === 'table' && renderTableConfig()}

        <div className='mt-auto pt-4 border-t border-border/50'>
          <div className='bg-primary/5 text-primary rounded-lg p-3 text-xs flex items-start gap-2'>
            <Info size={14} className='mt-0.5 shrink-0' />
            <span className='leading-relaxed'>
              Weekly Planning Studio widgets aggregate plan records dynamically.
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Chart Renderer Component
interface StudioChartRendererProps {
  item: DashboardItem
  onPageChange?: (id: string, page: number) => void
}

export const StudioChartRenderer: React.FC<StudioChartRendererProps> = ({
  item,
  onPageChange,
}) => {
  const data = item.chartData || []
  const hasData = data.length > 0

  if (!hasData) {
    return (
      <div className='flex flex-col items-center justify-center h-full text-muted-foreground p-4 text-center border-2 border-dashed border-border rounded-lg'>
        <LayoutIcon size={28} className='mb-2 opacity-20' />
        <p className='text-sm font-medium'>No data to display</p>
        <p className='text-xs mt-1 opacity-60'>
          Configure widget in config panel
        </p>
      </div>
    )
  }

  switch (item.type) {
    case 'bar': {
      const metricLabels = (item.metricFields || []).map(f => getFieldLabel(f))
      return (
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray='3 3'
              vertical={false}
              stroke='hsl(var(--border))'
            />
            <XAxis
              dataKey='name'
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              interval={0}
              angle={-20}
              textAnchor='end'
              height={50}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted) / 0.5)' }}
              contentStyle={{
                borderRadius: '10px',
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
                fontSize: '11px',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '10px' }} />
            {metricLabels.map((label, idx) => (
              <Bar
                key={label}
                dataKey={label}
                fill={COLORS[idx % COLORS.length] || '#6366f1'}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )
    }

    case 'line': {
      const metricLabels = (item.metricFields || []).map(f => getFieldLabel(f))
      return (
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            data={data}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray='3 3'
              vertical={false}
              stroke='hsl(var(--border))'
            />
            <XAxis
              dataKey='name'
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              interval={0}
              angle={-20}
              textAnchor='end'
              height={50}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                borderRadius: '10px',
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
                fontSize: '11px',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '10px' }} />
            {metricLabels.map((label, idx) => (
              <Line
                key={label}
                type='monotone'
                dataKey={label}
                stroke={COLORS[idx % COLORS.length] || '#6366f1'}
                strokeWidth={2}
                dot={{ strokeWidth: 1.5, r: 3, fill: 'hsl(var(--card))' }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )
    }

    case 'pie': {
      return (
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Pie
              data={data}
              cx='50%'
              cy='50%'
              innerRadius='50%'
              outerRadius='75%'
              paddingAngle={2}
              dataKey='value'
            >
              {data.map((_: ChartDataRow, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length] || '#6366f1'}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: '10px',
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
                fontSize: '11px',
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '10px' }}
              formatter={(value: string) => (
                <span className='text-foreground'>{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      )
    }

    case 'scorecard': {
      const sc = data[0]
      if (!sc) return null
      const label = sc.label || ''
      const formattedValue =
        typeof sc.value === 'number'
          ? sc.value.toLocaleString(undefined, { maximumFractionDigits: 1 })
          : sc.value

      return (
        <div className='flex flex-col items-start justify-center h-full px-3'>
          <p className='text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-1'>
            {label}
          </p>
          <h3 className='text-3xl font-extrabold text-foreground tracking-tight'>
            {formattedValue}
            {label.toLowerCase().includes('hrs') && (
              <span className='text-base font-normal text-muted-foreground ml-1'>
                hrs
              </span>
            )}
          </h3>
          <p className='text-xs text-muted-foreground mt-1.5 flex items-center gap-1'>
            <TrendingUp size={12} className='text-emerald-500' />
            <span className='text-emerald-500 font-semibold'>Live</span>
            <span>Allocations</span>
          </p>
        </div>
      )
    }

    case 'table': {
      const columns = item.selectedColumns || []
      const totalPages =
        Math.ceil((item.totalRows || 0) / (item.limit || 10)) || 1

      return (
        <div className='flex flex-col h-full'>
          <div className='flex-1 overflow-auto border border-border rounded-lg bg-card relative pointer-events-auto'>
            <Table>
              <TableHeader className='bg-muted/50 sticky top-0 z-10'>
                <TableRow className='border-border'>
                  {columns.map(col => (
                    <TableHead
                      key={col}
                      className='text-[10px] font-bold text-foreground uppercase whitespace-nowrap tracking-wide py-2'
                    >
                      {getFieldLabel(col)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row: ChartDataRow, idx: number) => (
                  <TableRow
                    key={idx}
                    className='border-border hover:bg-muted/30'
                  >
                    {columns.map(col => (
                      <TableCell
                        key={col}
                        className='text-xs py-1.5 whitespace-nowrap text-foreground'
                      >
                        {row[col] ?? '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className='flex items-center justify-between pt-1.5 pointer-events-auto'>
            <p className='text-[10px] text-muted-foreground'>
              Page {item.page || 1} of {totalPages} · {item.totalRows || 0} rows
            </p>
            <div className='flex gap-1'>
              <Button
                variant='outline'
                size='icon'
                className='h-6 w-6'
                disabled={(item.page || 1) <= 1}
                onClick={() => onPageChange?.(item.i, (item.page || 1) - 1)}
              >
                <ChevronLeft size={12} />
              </Button>
              <Button
                variant='outline'
                size='icon'
                className='h-6 w-6'
                disabled={(item.page || 1) >= totalPages}
                onClick={() => onPageChange?.(item.i, (item.page || 1) + 1)}
              >
                <ChevronRight size={12} />
              </Button>
            </div>
          </div>
        </div>
      )
    }

    default:
      return <div className='text-muted-foreground'>Unknown Component</div>
  }
}

// Dashboard Canvas Component
interface DashboardCanvasProps {
  items: DashboardItem[]
  layouts: ResponsiveLayouts
  selectedItemId: string | null
  setSelectedItemId: (id: string | null) => void
  removeItem: (id: string, e: React.MouseEvent) => void
  onLayoutChange: (layout: Layout, allLayouts: ResponsiveLayouts) => void
  onPageChange: (id: string, page: number) => void
  isPreview?: boolean
}

const StudioDashboardCanvas: React.FC<DashboardCanvasProps> = ({
  items,
  layouts,
  selectedItemId,
  setSelectedItemId,
  removeItem,
  onLayoutChange,
  onPageChange,
  isPreview = false,
}) => {
  const [zoom, setZoom] = useState(1)
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5))
  const handleResetZoom = () => setZoom(1)

  return (
    <div
      className='flex-1 overflow-auto w-full relative bg-background'
      onClick={() => setSelectedItemId(null)}
    >
      <div className='sticky top-4 right-4 z-50 flex justify-end gap-2 px-8 pointer-events-none'>
        <div className='flex items-center gap-1 bg-card/85 backdrop-blur-sm border border-border p-1.5 rounded-xl shadow-sm pointer-events-auto'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 text-muted-foreground hover:text-primary'
            onClick={handleZoomOut}
            title='Zoom Out'
          >
            <ZoomOut size={16} />
          </Button>
          <div className='w-12 text-center text-xs font-bold text-foreground'>
            {Math.round(zoom * 100)}%
          </div>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 text-muted-foreground hover:text-primary'
            onClick={handleZoomIn}
            title='Zoom In'
          >
            <ZoomIn size={16} />
          </Button>
          <div className='w-px h-4 bg-border mx-1' />
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 text-muted-foreground hover:text-primary'
            onClick={handleResetZoom}
            title='Reset Zoom'
          >
            <RotateCcw size={16} />
          </Button>
        </div>
      </div>

      <div className='p-8 mx-auto pb-32 min-w-fit'>
        {!isPreview && (
          <div className='mb-6 flex justify-between items-center max-w-[1200px] mx-auto'>
            <div>
              <h2 className='text-2xl font-bold text-foreground tracking-tight'>
                Planning Studio Canvas
              </h2>
              <p className='text-sm text-muted-foreground mt-1'>
                Build custom dashboard layouts from your Weekly resource plans.
                Drag and resize widgets.
              </p>
            </div>
          </div>
        )}

        <div
          className={`transition-transform duration-200 ease-out origin-top border border-border bg-card shadow-xl rounded-xl ${isPreview ? 'mt-12' : ''}`}
          style={{
            transform: `scale(${zoom})`,
            width: '1200px',
            margin: '0 auto',
            backgroundImage: isPreview
              ? 'none'
              : 'radial-gradient(hsl(var(--border)) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            minHeight: '800px',
          }}
        >
          <div className='min-h-[600px]'>
            <ResponsiveGridLayout
              className='layout'
              layouts={layouts}
              width={1200}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={30}
              onLayoutChange={onLayoutChange}
              dragConfig={{ enabled: !isPreview, handle: '.drag-handle' }}
              resizeConfig={{ enabled: !isPreview }}
              margin={[16, 16]}
            >
              {items.map(item => {
                const isSelected = selectedItemId === item.i && !isPreview
                return (
                  <div
                    key={item.i}
                    className={`group relative pt-8 h-full ${isPreview ? '' : 'cursor-pointer'}`}
                    onClick={e => {
                      if (isPreview) return
                      e.stopPropagation()
                      setSelectedItemId(item.i)
                    }}
                  >
                    <Card
                      className={`absolute inset-0 flex flex-col h-full overflow-hidden shadow-sm transition-all duration-200 border bg-card ${
                        isSelected
                          ? 'ring-2 ring-primary border-transparent shadow-lg'
                          : 'border-border ring-0'
                      } ${isPreview ? '' : 'hover:shadow-md'}`}
                    >
                      <CardHeader
                        className={`px-4 py-2 flex flex-row items-center justify-between transition-colors border-b border-border space-y-0 h-10 ${
                          isPreview
                            ? 'bg-card border-transparent'
                            : 'drag-handle cursor-move bg-muted/30 hover:bg-muted/50'
                        } ${isSelected ? 'bg-primary/5 border-primary/20' : ''}`}
                      >
                        <CardTitle
                          className={`text-[12px] font-bold uppercase tracking-wider ${
                            isSelected ? 'text-primary' : 'text-foreground/70'
                          }`}
                        >
                          {item.title}
                        </CardTitle>
                        {!isPreview && (
                          <button
                            type='button'
                            onClick={e => removeItem(item.i, e)}
                            className='text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 outline-none p-1 rounded hover:bg-destructive/10 z-20'
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </CardHeader>

                      <CardContent className='flex-1 p-4 pb-4 overflow-hidden min-h-0 relative bg-background/30'>
                        <StudioChartRenderer
                          item={item}
                          onPageChange={onPageChange}
                        />
                      </CardContent>
                    </Card>
                  </div>
                )
              })}
            </ResponsiveGridLayout>
          </div>
        </div>
      </div>
    </div>
  )
}

// Sidebar Component
interface SidebarProps {
  addItem: (type: ComponentType) => void
}

const WIDGET_ITEMS: {
  type: ComponentType
  label: string
  icon: React.ReactNode
  description: string
}[] = [
  {
    type: 'scorecard',
    label: 'Scorecard',
    icon: <LayoutIcon size={18} />,
    description: 'KPI metric card',
  },
  {
    type: 'bar',
    label: 'Bar Chart',
    icon: <BarChart2 size={18} />,
    description: 'Compare values across groups',
  },
  {
    type: 'line',
    label: 'Line Chart',
    icon: <LineChartIcon size={18} />,
    description: 'Track trends over groups',
  },
  {
    type: 'pie',
    label: 'Pie Chart',
    icon: <PieChartIcon size={18} />,
    description: 'Show distribution breakdown',
  },
  {
    type: 'table',
    label: 'Data Table',
    icon: <TableIcon size={18} />,
    description: 'Detailed plan list view',
  },
]

const StudioSidebar: React.FC<SidebarProps> = ({ addItem }) => {
  return (
    <div className='w-64 bg-card border-r border-border flex flex-col shadow-sm z-10 shrink-0'>
      <div className='p-5 border-b border-border flex items-center gap-3 bg-muted/10'>
        <div className='p-2 bg-primary/10 text-primary rounded-lg shadow-sm'>
          <Sparkles size={18} />
        </div>
        <div>
          <h1 className='text-sm font-extrabold text-foreground'>
            Planning Studio
          </h1>
          <p className='text-[10px] text-muted-foreground uppercase font-bold tracking-wider'>
            Dashboard Builder
          </p>
        </div>
      </div>

      <div className='flex-1 p-4 overflow-y-auto'>
        <h2 className='text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3 px-1'>
          Add Widgets
        </h2>
        <div className='space-y-2'>
          {WIDGET_ITEMS.map(({ type, label, icon, description }) => (
            <button
              key={type}
              onClick={() => addItem(type)}
              className='w-full flex items-start gap-3 p-3 rounded-xl border border-border/60 bg-background hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 text-left group'
            >
              <div className='p-1.5 rounded-lg bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors shrink-0 mt-0.5 shadow-sm'>
                {icon}
              </div>
              <div className='min-w-0'>
                <p className='text-sm font-bold text-foreground group-hover:text-primary transition-colors'>
                  {label}
                </p>
                <p className='text-[11px] text-muted-foreground leading-tight mt-0.5'>
                  {description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// Main Planning Studio Component
interface WeeklyPlanningStudioProps {
  plans?: StudioPlanRow[]
  isLoading?: boolean
}

export default function WeeklyPlanningStudio({
  plans: initialPlans = [],
  isLoading: initialLoading = false,
}: WeeklyPlanningStudioProps) {
  const [items, setItems] = useState<DashboardItem[]>([])
  const [layouts, setLayouts] = useState<ResponsiveLayouts>({ lg: [] })
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [isPreview, setIsPreview] = useState(false)
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().startOf('isoWeek').toDate(), // Always Monday
    to: dayjs().endOf('isoWeek').toDate(), // Sunday
  })

  const [plans, setPlans] = useState<StudioPlanRow[]>(initialPlans)
  const [isLoading, setIsLoading] = useState(initialLoading)

  const fetchPlans = useCallback(async () => {
    setIsLoading(true)
    try {
      const headers = prepareHeaders(new Headers())
      const axiosHeaders: Record<string, string> = {}
      headers.forEach((value, key) => {
        axiosHeaders[key] = value
      })

      const params: Record<string, string> = {}
      if (dateRange.from) {
        const monday = dayjs(dateRange.from)
          .startOf('isoWeek')
          .format('YYYY-MM-DD')
        params['weekStart'] = monday
        params['week_start'] = monday // Compatibility for backend validation
      }
      if (dateRange.to) {
        params['weekEnd'] = dayjs(dateRange.to).format('YYYY-MM-DD')
        params['week_end'] = dayjs(dateRange.to).format('YYYY-MM-DD')
      }

      const response = await axios.get<WeeklyPlanningPlansResponse>(
        `${BASE_URL}/weekly-planning/plans`,
        {
          params,
          headers: axiosHeaders,
        }
      )

      if (response.data.success) {
        const rawData = response.data.data || []

        // Check if data is nested (range response) or flat (single week)
        const isNested = rawData.some(isRangeUser)

        if (isNested) {
          // Flatten nested structure: data[] -> user -> weeks[]
          const flattened: StudioPlanRow[] = []
          rawData.filter(isRangeUser).forEach(user => {
            const userWeeks = user.weeks || []
            userWeeks.forEach((week, index) => {
              flattened.push(
                normalizePlanRow({
                  ...week,
                  user_id: user.user_id,
                  member_name: user.member_name,
                  team_id: user.team_id,
                  team: user.team || 'Unassigned',
                  // Avoid overcounting achieved hours in range sums
                  achieved_billable_hrs:
                    index === 0 && user.achieved_billable_hrs != null
                      ? String(user.achieved_billable_hrs)
                      : '0',
                  week_label: week.week_start
                    ? dayjs(week.week_start).format('MMM DD')
                    : 'Current',
                })
              )
            })
          })
          setPlans(flattened)
        } else {
          // Already flat (old format or single week)
          setPlans(
            rawData.filter(isApiRow).map(row =>
              normalizePlanRow({
                ...row,
                week_label: row.week_start
                  ? dayjs(row.week_start).format('MMM DD')
                  : 'Current',
              })
            )
          )
        }
      }
    } catch {
      return
    } finally {
      setIsLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  // Compute filtered plans based on selected date range
  const filteredPlans = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return plans
    return plans.filter(p => {
      const dateStr = p.week_start || p.est_bench_date
      if (!dateStr) return false
      const date = dayjs(dateStr)
      const fromDate = dateRange.from ? dayjs(dateRange.from) : null
      const toDate = dateRange.to ? dayjs(dateRange.to) : null
      const afterFrom = fromDate
        ? date.isAfter(fromDate, 'day') || date.isSame(fromDate, 'day')
        : true
      const beforeTo = toDate
        ? date.isBefore(toDate, 'day') || date.isSame(toDate, 'day')
        : true
      return afterFrom && beforeTo
    })
  }, [plans, dateRange])

  useEffect(() => {
    if (isLoading) return
    setItems(prev => prev.map(item => recomputeItemData(item, filteredPlans)))
  }, [filteredPlans, isLoading])

  const addItem = useCallback(
    (type: ComponentType) => {
      const newId = Math.random().toString(36).substr(2, 9)
      const titles: Record<ComponentType, string> = {
        bar: 'Bar Chart',
        line: 'Line Chart',
        pie: 'Pie Chart',
        scorecard: 'Scorecard',
        table: 'Data Table',
      }

      const newItem: DashboardItem = {
        i: newId,
        type,
        title: titles[type],
        dimensionField: 'team',
        metricFields: ['est_billing_hrs'],
        aggregation: 'sum',
        labelField: 'team',
        valueField: 'est_billing_hrs',
        selectedColumns: [
          'member_name',
          'team',
          'plan_status',
          'est_billing_hrs',
          'est_non_billing_hrs',
        ],
        page: 1,
        limit: 10,
        chartData: [],
        scorecardMetric: 'est_billing_hrs',
        scorecardAggregation: 'sum',
      }

      setItems(prev => [...prev, recomputeItemData(newItem, plans)])
      setSelectedItemId(newId)

      setLayouts(prev => ({
        ...prev,
        lg: [
          ...(prev['lg'] || []),
          {
            i: newId,
            x: 0,
            y: Infinity,
            w: type === 'scorecard' ? 3 : type === 'table' ? 8 : 6,
            h: type === 'scorecard' ? 4 : type === 'table' ? 12 : 9,
          },
        ],
      }))
    },
    [plans]
  )

  const removeItem = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation()
      setItems(prev => prev.filter(item => item.i !== id))
      setLayouts(prev => ({
        ...prev,
        lg: (prev['lg'] || []).filter(l => l.i !== id),
      }))
      if (selectedItemId === id) setSelectedItemId(null)
    },
    [selectedItemId]
  )

  const handleItemUpdate = useCallback(
    (id: string, updates: Partial<DashboardItem>) => {
      setItems(prev =>
        prev.map(item => {
          if (item.i !== id) return item
          const updated = { ...item, ...updates }
          return recomputeItemData(updated, plans)
        })
      )
    },
    [plans]
  )

  const handlePageChange = useCallback(
    (id: string, page: number) => {
      setItems(prev =>
        prev.map(item => {
          if (item.i !== id) return item
          const updated = { ...item, page }
          return recomputeItemData(updated, plans)
        })
      )
    },
    [plans]
  )

  const onLayoutChange = useCallback(
    (_layout: Layout, allLayouts: ResponsiveLayouts) => {
      setLayouts(allLayouts)
    },
    []
  )

  const selectedItem = items.find(item => item.i === selectedItemId)

  if (isLoading && plans.length === 0) {
    return (
      <div className='flex items-center justify-center h-[calc(100vh-220px)] border border-border rounded-xl mt-4 bg-card shadow-sm'>
        <div className='flex flex-col items-center gap-3'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
          <p className='text-sm text-muted-foreground font-medium'>
            Loading weekly resource planning plans...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex h-[calc(100vh-220px)] bg-background overflow-hidden font-sans relative border border-border rounded-xl mt-4 shadow-sm'>
      {/* Top Header Controls */}
      <div className='absolute top-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 bg-card/90 backdrop-blur-md border border-border p-1 rounded-full shadow-lg'>
        <button
          onClick={() => setIsPreview(false)}
          className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
            !isPreview
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Edit
        </button>
        <button
          onClick={() => {
            setIsPreview(true)
            setSelectedItemId(null)
          }}
          className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
            isPreview
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Preview
        </button>
        <div className='w-px h-4 bg-border mx-1' />
        <ReusableDateRangePicker
          value={dateRange}
          onSelect={setDateRange}
          placeholder='Filter by date range'
          className='mr-2'
        />

        <span className='text-[10px] text-muted-foreground font-bold px-2 uppercase tracking-wide'>
          {filteredPlans.length} plans loaded
        </span>
        {isLoading && (
          <Loader2 className='h-3 w-3 animate-spin text-primary ml-1' />
        )}
      </div>

      {/* Sidebar Toggle */}
      {!isPreview && (
        <button
          onClick={() => setIsSidebarVisible(true)}
          className='absolute left-4 top-4 z-50 p-2 bg-card border border-border rounded-lg shadow-md hover:bg-muted transition-all text-muted-foreground'
          title='Show Sidebar'
        >
          <PanelLeftOpen size={20} />
        </button>
      )}

      {/* Left Sidebar */}
      <div
        className={`transition-all duration-300 ease-in-out flex shrink-0 ${
          isSidebarVisible && !isPreview
            ? 'w-64 opacity-100'
            : 'w-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className='w-64 flex flex-col h-full bg-card border-r border-border relative'>
          <button
            onClick={() => setIsSidebarVisible(false)}
            className='absolute -right-3 top-1/2 -translate-y-1/2 z-50 p-1 bg-card border border-border rounded-full shadow-sm hover:bg-muted transition-all text-muted-foreground hover:text-foreground'
            title='Hide Sidebar'
          >
            <ChevronLeft size={14} />
          </button>
          <StudioSidebar addItem={addItem} />
        </div>
      </div>

      {/* Main Canvas */}
      <StudioDashboardCanvas
        items={items}
        layouts={layouts}
        selectedItemId={selectedItemId}
        setSelectedItemId={setSelectedItemId}
        removeItem={removeItem}
        onLayoutChange={onLayoutChange}
        onPageChange={handlePageChange}
        isPreview={isPreview}
      />

      {/* Right Config Panel */}
      {!isPreview && selectedItemId && selectedItem && (
        <ConfigPanel selectedItem={selectedItem} onUpdate={handleItemUpdate} />
      )}
    </div>
  )
}
